import _ from 'lodash';
import isEqual from 'lodash/isEqual';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import SubmitCompleted from './SubmitCompleted';
import { getTranslatedEntityName } from './utils';
import { setTokenStatus, showIdpView } from '../../actions/AuthActions';
import { setOpenDetailFormTabEntityDisplay } from '../../actions/EntityDisplayActions';
import { setCustomLongLoadPageMessage } from '../../actions/LongLoadPageActions';
import {
  batchUpdate,
  bulkUpload,
  onSubmitEnd,
  saveEntityToLocalStorage,
  savePublicDraft,
  setFinishSubmit,
  submitEntity,
  submitRequestPrint,
} from '../../actions/WorkflowActions';
import LOGIN_METHODS from '../../constants/loginMethodsConstants';
import * as Urls from '../../constants/Urls';
import { usePrevious } from '../../custom-hooks';
import { clearFormDesigner } from '../../form-designer/actions/form-manager.actions';
import { FORM_KEYS } from '../../form-designer/constants/form-manager.constants';
import { getNetworkStatus } from '../../form-designer/selectors/common.selectors';
import { getSignOffStepName } from '../../form-designer/selectors/runtime.selectors';
import { clearValidationStorage } from '../../form-designer/utils/validationStorage.utils';
import {
  selectIsTokenUpdated,
  selectLoginMethod,
  selectShowSignOffDialog,
} from '../../selectors/AuthSelectors';
import { getWorkflow } from '../../selectors/WorkflowSelectors';
import { isNilOrEmpty } from '../../utils/ramdaUtils';
import AuthBlueBanner from '../AuthBlueBanner';

const SubmitFormDialog = ({ open }) => {
  const [isSigned, setIsSigned] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [translatedEntityNames, setTranslatedEntityNames] = useState([]);

  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const workflow = useSelector(getWorkflow);
  const {
    finishSubmit,
    formIsSubmitting,
    formSubmitType,
    latestSubmitType,
    requiresSignature,
    submitWorkflowApiCompleted,
    submitWorkflowResult,
    workFlowSteps,
    signOffMeaning: signOffMeaningKey,
    targetForm: formKey,
  } = workflow;
  const showSignOffDialog = useSelector(selectShowSignOffDialog);
  const loginMethod = useSelector(selectLoginMethod);
  const isTokenUpdated = useSelector(selectIsTokenUpdated);
  const errors = useSelector((state) => state.errors);
  const isOnline = useSelector(getNetworkStatus);
  const signOffStepName = useSelector((state) => getSignOffStepName(state, { formKey }));
  const customLongLoadMessage = useSelector((state) => state.longLoadPageMessage.customMessage);

  const prevSubmitWorkflowResult = usePrevious(submitWorkflowResult);

  const isSSOLoginMethod = isEqual(LOGIN_METHODS.SAML, loginMethod);
  const shouldSubmitFormAsSAMLUser = isSSOLoginMethod && isTokenUpdated && !showSignOffDialog;
  const hasAutomaticNav = !isNilOrEmpty(submitWorkflowResult?.goToStep);
  const hasErrors = errors.length > 0;

  const translateEntityNames = async (submitWorkflowResult) => {
    const entities = submitWorkflowResult?.entities ?? [];
    const translatedEntityNames = await Promise.all(
      entities.map((entity) => getTranslatedEntityName(entity))
    );
    return setTranslatedEntityNames(translatedEntityNames);
  };

  const resetState = () => {
    setIsSigned(false);
    setSubmitted(false);
    setTranslatedEntityNames([]);
  };

  const submitFormByType = (password = '') => {
    switch (formSubmitType) {
      case 'sign-off':
        // Sign off is completed, saving changes with password from sign off screen ...
        dispatch(submitEntity(password, true)).then(() => setSubmitted(true));
        break;

      case 'bulk-upload':
        dispatch(bulkUpload(formKey)).then(() => setSubmitted(true));
        break;

      case 'save-public-draft':
        dispatch(savePublicDraft()).then(() => setSubmitted(true));
        break;

      case 'submit-control-print':
        dispatch(submitRequestPrint(qs.parse(search)));
        break;

      case 'batch-submit':
        dispatch(batchUpdate()).then(() => setSubmitted(true));
        break;
      case 'save-offline':
        dispatch(saveEntityToLocalStorage()).then(() => {
          history.push(Urls.HOME);
        });
        break;

      default:
        dispatch(submitEntity('', false)).then(() => setSubmitted(true));
        break;
    }

    setIsSigned(true);
  };

  const handleDialogClose = (e, reason) => {
    if (reason === 'backdropClick') {
      e.preventDefault();
      return;
    }

    dispatch(onSubmitEnd());
    dispatch(setFinishSubmit(false));
    customLongLoadMessage && dispatch(setCustomLongLoadPageMessage(''));
    resetState();
  };

  const handleSubmitWorkflowResult = () => {
    if (!_.isPlainObject(submitWorkflowResult)) {
      return;
    }

    const { goToStep, workFlowSteps, entities } = _.pick(submitWorkflowResult, [
      'goToStep',
      'workFlowSteps',
      'entities',
    ]);

    if (isNilOrEmpty(goToStep)) {
      translateEntityNames(submitWorkflowResult);
      return;
    }

    handleDialogClose();

    if (goToStep === 'Exit') {
      history.push(Urls.HOME);
      return;
    }

    const nextStep = workFlowSteps?.find((w) => w.stepName === goToStep);

    if (!_.isNil(nextStep)) {
      clearValidationStorage();
      dispatch(clearFormDesigner(FORM_KEYS.STEP));
      history.push(
        `${Urls.ENTITY_DISPLAY}?${qs.stringify(
          _.pick(nextStep, ['itmId', 'entityType', 'formKey', 'workflowId', 'stepId'])
        )}`
      );
      return;
    }

    const entity = entities?.find((e) => e.entityName === goToStep);

    const goToEntityDetail = (e) => {
      clearValidationStorage();
      dispatch(clearFormDesigner(FORM_KEYS.STEP));
      dispatch(setOpenDetailFormTabEntityDisplay(true));
      history.push(
        `${Urls.ENTITY_DISPLAY}?${qs.stringify(
          _.pick(e, ['itmId', 'entityType', 'entityName', 'formKey'])
        )}`
      );
    };

    if (!_.isNil(entity)) {
      goToEntityDetail(entity);
      return;
    }

    const detailEntity = _.head(entities);

    if (!_.isNil(detailEntity)) {
      goToEntityDetail(detailEntity);
    }
  };

  const renderSubmitCompleted = () => {
    return (
      <SubmitCompleted
        open={open}
        submitWorkflowApiCompleted={submitWorkflowApiCompleted}
        submitWorkflowResult={submitWorkflowResult}
        workFlowSteps={workFlowSteps}
        hasErrors={hasErrors}
        translatedEntityNames={translatedEntityNames}
        latestSubmitType={latestSubmitType}
        handleDialogClose={handleDialogClose}
      />
    );
  };

  useEffect(() => {
    if (finishSubmit) {
      handleSubmitWorkflowResult();
    }

    return () => {
      if (isSSOLoginMethod && isTokenUpdated) {
        dispatch(setTokenStatus({ isTokenUpdated: false }));
      }

      if (formIsSubmitting) {
        dispatch(onSubmitEnd());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      (isSigned || finishSubmit) &&
      submitWorkflowResult !== prevSubmitWorkflowResult &&
      isOnline
    ) {
      handleSubmitWorkflowResult();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSigned, finishSubmit, submitWorkflowResult, prevSubmitWorkflowResult, isOnline]);

  if (finishSubmit && !submitWorkflowApiCompleted) {
    return null;
  }

  if (finishSubmit && !hasAutomaticNav) {
    window.scrollTo(0, 0);
    return renderSubmitCompleted();
  }

  if (finishSubmit) {
    return null;
  }
  if (isSigned && !submitted) {
    return null;
  }
  if (submitted && hasAutomaticNav) {
    return null;
  }

  if (submitted) {
    window.scrollTo(0, 0);
    return renderSubmitCompleted();
  }

  if (hasErrors) {
    return null;
  }

  if (!requiresSignature || shouldSubmitFormAsSAMLUser) {
    // for steps with no auth validation - perform data post to BE
    submitFormByType(formSubmitType);
    return '';
  }

  if (isSSOLoginMethod && !isTokenUpdated) {
    setTimeout(() => {
      dispatch(showIdpView({ isSignOffStep: true }));
    });

    return null;
  }

  const submitButtonLabel =
    formSubmitType === 'sign-off' ||
    formSubmitType === 'submit-control-print' ||
    formSubmitType === 'batch-submit'
      ? 'Sign-Off'
      : 'Save';

  return (
    <AuthBlueBanner
      open={open}
      signOffMeaningKey={signOffMeaningKey}
      signOffStepName={signOffStepName}
      handleDialogClose={handleDialogClose}
      submitButtonLabel={submitButtonLabel}
      onAuthSuccess={submitFormByType}
      isWorkflow
    />
  );
};

export default SubmitFormDialog;
